/**
 * SectionCard — canonical card-wrapper for grouping form config blocks.
 *
 * Per SYSADMIN-PLATFORM-SPEC §11.6:
 *   - Card only for grouping logically separate config blocks
 *     (Identity, Compliance, Features). Not for wrapping tables (§11.5).
 *   - Inside cards: 2-column Label/Input grid with max-w-2xl form width
 *   - One save pattern: sticky bottom action bar at page level
 *
 * v1 source: ported from `components/admin/setting-field.tsx`, split
 * into its own file so consumers can import it without dragging in
 * the SettingField helper graph.
 *
 * @example
 * <SectionCard title="Compliance" icon={ShieldCheck} description="ITAR + CMMC L2 profile">
 *   <SettingField label="ITAR enabled">…</SettingField>
 *   <SettingField label="CMMC level">…</SettingField>
 * </SectionCard>
 *
 * @example danger variant
 * <SectionCard title="Tenant data" danger lockedBadge="Owner only">
 *   <SettingField label="Factory reset">…</SettingField>
 * </SectionCard>
 */

import type { ComponentType, ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Badge } from './badge';
import { cn } from '@/lib/utils';

export interface SectionCardProps {
  title: string;
  description?: string;
  icon?: ComponentType<{ className?: string }>;
  actions?: ReactNode;
  /** Danger: red accent + border */
  danger?: boolean;
  /** Locked by policy/tier badge */
  lockedBadge?: string;
  className?: string;
  children: ReactNode;
}

export function SectionCard({
  title, description, icon: Icon, actions, danger, lockedBadge, className, children,
}: SectionCardProps) {
  return (
    <Card className={cn(danger && 'border-red-500/40', className)}>
      <CardHeader className="pb-3 pt-4 px-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className={cn(
              'text-sm font-semibold flex items-center gap-1.5',
              danger && 'text-red-700 dark:text-red-300',
            )}>
              {Icon && <Icon className={cn('h-4 w-4', danger ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')} />}
              {title}
              {lockedBadge && (
                <Badge variant="outline" className="text-[9px] gap-1 h-5">
                  <Lock className="h-2.5 w-2.5" />
                  {lockedBadge}
                </Badge>
              )}
            </CardTitle>
            {description && (
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{description}</p>
            )}
          </div>
          {actions && <div className="shrink-0">{actions}</div>}
        </div>
      </CardHeader>
      <CardContent className="pt-0 pb-4 px-5 space-y-4">
        {children}
      </CardContent>
    </Card>
  );
}
